import {
  FixedFeeConfig,
  Adjustment,
  ReclassBalance,
} from "@amzn/fuse-accounting-central-datastore-client";
import { ReportRow } from "./types";
import { toNum, getFirstOfMonth } from "./utils";

export function computeReportRow(
  config: FixedFeeConfig,
  reportDate: string,
  reclassCutoff: string,
): ReportRow | null {
  const env = config.environment;
  const status = config.dealStatus;
  if (env !== "PRODUCTION" || status !== "ENABLED") return null;

  const dealStart = config.dealStartDate ?? "";
  const dealEnd = config.dealEndDate ?? "";
  const reportMonthStart = getFirstOfMonth(reportDate);

  // Deal must be active during report month
  if (dealEnd && dealEnd < reportMonthStart) return null;
  if (dealStart && dealStart > reportDate) return null;

  const contractPrice = toNum(config.contractPrice);
  const rb: ReclassBalance | undefined = config.reclassBalance;
  const hasReclass =
    rb != null &&
    (rb.unbilledAccountsReceivables != null || rb.longTermReceivables != null);

  const ltIssuance = hasReclass
    ? rb!.longTermReceivables != null
      ? toNum(rb!.longTermReceivables)
      : 0
    : 0;
  const stIssuance = hasReclass
    ? rb!.unbilledAccountsReceivables != null
      ? toNum(rb!.unbilledAccountsReceivables)
      : contractPrice - ltIssuance
    : contractPrice;

  // Invoices issued: invoice date (map key) <= report date
  const invoiceBreakdown = config.invoiceBreakdown ?? {};
  let invoicesIssued = 0;
  for (const [invoiceDate, detail] of Object.entries(invoiceBreakdown)) {
    if (invoiceDate <= reportDate) {
      invoicesIssued += toNum(detail.invoiceAmount);
    }
  }

  // Cumulative reclassification: sum receivableShiftAmount where adj date <= reclassCutoff
  const adjustments: Record<string, Adjustment> = config.adjustments ?? {};
  let cumulativeReclass = 0;
  for (const [adjDate, adj] of Object.entries(adjustments)) {
    if (adjDate <= reclassCutoff && adj.receivableShiftAmount != null) {
      cumulativeReclass += toNum(adj.receivableShiftAmount);
    }
  }

  const resellerId = config.dealConfig?.resellerIds?.[0] ?? "";

  // Invoice Processing: Automated if STANDARD + hasReclassBalance + executedAdj > 0
  const executedAdjCount = Object.values(adjustments).filter(
    (a) => a.adjustmentAmount != null,
  ).length;
  const isAutomated =
    config.fixedFeeType === "STANDARD" && hasReclass && executedAdjCount > 0;

  // Partially Manual: automated but had a manual period (leading receivableShiftAmount=0)
  let invoiceProcessing = "Manual";
  if (isAutomated) {
    let leadingZeros = 0;
    for (const adjDate of Object.keys(adjustments).sort()) {
      const adj = adjustments[adjDate];
      if (adj.adjustmentAmount == null) continue;
      const rsa = adj.receivableShiftAmount;
      if (rsa != null && toNum(rsa) === 0) {
        leadingZeros++;
      } else {
        break;
      }
    }
    invoiceProcessing = leadingZeros > 1 ? "Partially Manual" : "Automatic";
  }

  return {
    fixedFeeId: config.fixedFeeId ?? "",
    companyCode: "",
    resellerId,
    dealStartDate: dealStart,
    dealEndDate: dealEnd,
    invoiceProcessing,
    contractPrice,
    currencyCode: config.contractCurrencyCode ?? "",
    fxRate: 1.0, // TODO: populate from ARD functionalCurrency/FX rate
    funcCurrency: config.contractCurrencyCode ?? "", // Same as contract until ARD join
    stIssuance,
    ltIssuance,
    cumulativeReclass,
    invoicesIssued,
    stEnding: stIssuance + cumulativeReclass - invoicesIssued,
    ltEnding: ltIssuance - cumulativeReclass,
  };
}
